/**
 * System interface and scheduler.
 *
 * Systems declare their loop phase, sub-phase, and priority.
 * The scheduler sorts systems by (phase, subPhase, priority) for deterministic execution.
 */

import type { LoopPhase, SubPhase } from '../shared/types.js';
import type { ComponentDef } from './Component.js';
import type { World } from './World.js';
import { Logger } from '../core/Logger.js';

// ─── System Interface ───────────────────────────────────────────────────────────

export interface ISystem {
  readonly name: string;
  readonly phase: LoopPhase;
  readonly subPhase: SubPhase;
  readonly priority: number;
  readonly reads?: readonly ComponentDef[];
  readonly writes?: readonly ComponentDef[];

  init?(world: World): void;
  execute(dt: number, world: World): void;
  dispose?(): void;
}

// ─── System Schedule Entry (debug) ──────────────────────────────────────────────

export interface SystemScheduleEntry {
  name: string;
  phase: LoopPhase;
  subPhase: SubPhase;
  priority: number;
}

// ─── Scheduler ──────────────────────────────────────────────────────────────────

export class SystemScheduler {
  private systems: ISystem[] = [];
  private sorted = false;
  /**
   * Cache of pre-filtered system arrays keyed by `phase * 256 + subPhase`.
   * Runphase walks the seven loop phases × three sub-phases every frame
   * (~21 lookups/frame at 60 Hz); without this cache each lookup
   * allocated a fresh array and re-scanned the full systems list.
   * Invalidated on add/remove.
   */
  private _subPhaseCache = new Map<number, ISystem[]>();
  private _phaseCache = new Map<number, ISystem[]>();

  private _invalidateCache(): void {
    this._subPhaseCache.clear();
    this._phaseCache.clear();
  }

  add(system: ISystem): void {
    if (this.systems.some(s => s.name === system.name)) {
      Logger.warn('SystemScheduler', `Duplicate system name "${system.name}" — skipping`);
      return;
    }
    this.systems.push(system);
    this.sorted = false;
    this._invalidateCache();
  }

  remove(name: string): ISystem | undefined {
    const idx = this.systems.findIndex(s => s.name === name);
    if (idx === -1) return undefined;
    const [removed] = this.systems.splice(idx, 1);
    this._invalidateCache();
    return removed;
  }

  private ensureSorted(): void {
    if (this.sorted) return;
    this.systems.sort((a, b) => {
      if (a.phase !== b.phase) return a.phase - b.phase;
      if (a.subPhase !== b.subPhase) return a.subPhase - b.subPhase;
      return a.priority - b.priority;
    });
    this.sorted = true;
    // Sort order changed so prior filtered arrays are stale even though
    // they still contain the same system references.
    this._invalidateCache();
  }

  /**
   * Get all systems for a given phase, sorted by subPhase and priority.
   * Result is cached until add/remove invalidates the schedule; callers
   * MUST NOT mutate the returned array.
   */
  getSystemsForPhase(phase: LoopPhase): ISystem[] {
    this.ensureSorted();
    const cached = this._phaseCache.get(phase);
    if (cached) return cached;
    const result: ISystem[] = [];
    for (const sys of this.systems) {
      if (sys.phase === phase) result.push(sys);
    }
    this._phaseCache.set(phase, result);
    return result;
  }

  /**
   * Get all systems for a phase/subPhase, sorted by priority.
   * Result is cached — see getSystemsForPhase contract.
   */
  getSystemsForSubPhase(phase: LoopPhase, subPhase: SubPhase): ISystem[] {
    this.ensureSorted();
    const key = phase * 256 + subPhase;
    const cached = this._subPhaseCache.get(key);
    if (cached) return cached;
    const result: ISystem[] = [];
    for (const sys of this.systems) {
      if (sys.phase === phase && sys.subPhase === subPhase) result.push(sys);
    }
    this._subPhaseCache.set(key, result);
    return result;
  }

  getSchedule(): SystemScheduleEntry[] {
    this.ensureSorted();
    return this.systems.map(s => ({
      name: s.name,
      phase: s.phase,
      subPhase: s.subPhase,
      priority: s.priority,
    }));
  }

  initAll(world: World): void {
    this.ensureSorted();
    for (const sys of this.systems) {
      try {
        sys.init?.(world);
      } catch (err) {
        Logger.error('SystemScheduler', `System "${sys.name}" init failed: ${err}`);
      }
    }
  }

  disposeAll(): void {
    // Dispose in reverse of init order (ensureSorted) so that systems
    // depending on earlier-initialized systems tear down first.
    this.ensureSorted();
    for (let i = this.systems.length - 1; i >= 0; i--) {
      try {
        this.systems[i].dispose?.();
      } catch (err) {
        Logger.error('SystemScheduler', `System "${this.systems[i].name}" dispose failed: ${err}`);
      }
    }
    this.systems.length = 0;
    this._invalidateCache();
  }
}
